import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Switch, Collapse, Select, Checkbox, Tag } from 'antd';
import './SideMenu.scss';

const { Panel } = Collapse;

const tagRender = props => {
  const { label, closable, onClose } = props;
  return (
    <Tag
      closable={closable}
      onClose={onClose}
      style={{ whiteSpace: 'normal', height: 'auto', maxWidth: '100%' }}
    >
      {label}
    </Tag>
  );
};

tagRender.propTypes = {
  label: PropTypes.string.isRequired,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
};

tagRender.defaultProps = {
  closable: false,
  onClose: () => {},
};
const Option = Select.Option;

class SideMenu extends React.Component {

  _formatClassifications = (json, keyName) => {
    const array = [];
    // List of NAICS keys to apply numeric sorting
    const naicsKeys = [
      'naicsFoundryCodesList',
      'naicsMachineCodesList',
      'naicsForgingCodesList',
      // Add more NAICS keys here if needed
    ];
    if (Array.isArray(json)) {
      let keys = json;
      // If this is a NAICS key, sort numerically
      if (naicsKeys.includes(keyName)) {
        keys = [...json].sort((a, b) => {
          // Numeric sort, handle possible hyphens
          const [aPrefix, aSuffix] = a.split('-');
          const [bPrefix, bSuffix] = b.split('-');
          const prefixComparison = parseInt(aPrefix, 10) - parseInt(bPrefix, 10);
          if (prefixComparison === 0) {
            return parseInt(aSuffix || '0', 10) - parseInt(bSuffix || '0', 10);
          }
          return prefixComparison;
        });
      }
      keys.forEach((key) => {
        if (
          this.props.supplierAggs &&
          this.props.supplierAggs[keyName] &&
          this.props.supplierAggs[keyName][key]
        ) {
          array.push({
            label: `${key} (${this.props.supplierAggs[keyName][key]})`,
            value: key,
          });
        }
      })
    } else {
      let entries = Object.entries(json);
      // If this is a NAICS key, sort numerically by key
      if (naicsKeys.includes(keyName)) {
        entries = [...entries].sort(([a], [b]) => {
          const [aPrefix, aSuffix] = a.split('-');
          const [bPrefix, bSuffix] = b.split('-');
          const prefixComparison = parseInt(aPrefix, 10) - parseInt(bPrefix, 10);
          if (prefixComparison === 0) {
            return parseInt(aSuffix || '0', 10) - parseInt(bSuffix || '0', 10);
          }
          return prefixComparison;
        });
      }
      entries.forEach(([key, value]) => {
        if (
          this.props.supplierAggs &&
          this.props.supplierAggs[keyName] &&
          this.props.supplierAggs[keyName][key]
        ) {
          array.push({
            label: `${value} (${this.props.supplierAggs[keyName][key]})`,
            value: key,
          });
        }
      });
    }
    return array;
  };

  render() {
    const {
      onChange,
      supplierSearchCriteria,
      supplierSearchMenuSwitch,
      supplierSearchMenuSelect,
      supplierSearchMenuCamelCase,
    } = this.props;

    const keyMap = {
      "naicsFoundryCodesList": "Foundry",
      // "naicsCastingCodesList": "Casting",
      "naicsMachineCodesList": "Machine Shop",
      "naicsForgingCodesList": "Forging",
      "Business Classification": "Business Classification"
    };
    const priority = [
      "naicsFoundryCodesList",
      // "naicsCastingCodesList",
      "naicsMachineCodesList",
      "naicsForgingCodesList"
    ];
    // Ensure Business Classification is included in the menu select options
    const enhancedSupplierSearchMenuSelect = { ...supplierSearchMenuSelect };
    if (!enhancedSupplierSearchMenuSelect['Business Classification']) {
      // Add business classification options from config
      enhancedSupplierSearchMenuSelect['Business Classification'] = [
        "8A Program Participant",
        "Economically Disadvantaged Woman-Owned Small Business (EDWOSB)",
        "Foreign-Owned Business",
        "Minority Owned",
        "SBA Certified Hubzone Firm",
        "Service-Disabled Veteran-Owned Small Business (SDVOSB)",
        "Small Business",
        "Small Disadvantaged Business (SDB)",
        "Veteran Owned Small Business (VOSB)",
        "Woman Owned Small Business (WOSB)"
      ];
    }
    
    const allKeys = Object.keys(enhancedSupplierSearchMenuSelect);
    const orderedKeys = [
      ...priority.filter(key => allKeys.includes(key)),
      ...allKeys
        .filter(key => !priority.includes(key))
        .sort((a, b) => a.localeCompare(b))
    ];

    const switchPriority = [
      "Foundry",
      // "Casting",
      "Forging",
      "Machine Shop"
    ];
    // Get all items
    const allSwitchItems = supplierSearchMenuSwitch;
    // Build ordered list: priority first, then the rest
    const orderedSwitchItems = [
      ...switchPriority.filter(item => allSwitchItems.includes(item)),
      ...allSwitchItems.filter(item => !switchPriority.includes(item))
    ];

    // Define the industry type capabilities keys and display names
    // Ensure the four NAICS keys and Business Classification are always available in supplierSearchMenuCamelCase
    const ensuredCamelCase = { ...supplierSearchMenuCamelCase };
    [
      'naicsFoundryCodesList',
      // 'naicsCastingCodesList',
      'naicsForgingCodesList',
      'naicsMachineCodesList',
    ].forEach(key => {
      if (!ensuredCamelCase[key]) ensuredCamelCase[key] = key;
    });
    
    // Ensure Business Classification is available
    if (!ensuredCamelCase['Business Classification']) {
      ensuredCamelCase['Business Classification'] = 'bizClass';
    }
    // For Industry Type Capabilities, use the same logic as Search Capabilities for enabling/disabling and checked state
    const industryTypeList = [
      { display: 'Foundry', key: 'Foundry' },
      // { display: 'Casting', key: 'Casting' },
      { display: 'Forging', key: 'Forging' },
      { display: 'Machine Shop', key: 'Machine Shop' },
    ];
    // Remove industry type items from Search Capabilities
    const industryTypeDisplaySet = new Set(industryTypeList.map(({ key }) => key));
    const filteredSwitchItems = orderedSwitchItems.filter(item => !industryTypeDisplaySet.has(item));

    // Sort filteredSwitchItems alphabetically by display name (or key if not found)
    const sortedFilteredSwitchItems = [...filteredSwitchItems].sort((a, b) => {
      const aLabel = keyMap[a] || a;
      const bLabel = keyMap[b] || b;
      return aLabel.localeCompare(bLabel);
    });

    // Sort industryTypeList alphabetically by display name
    const sortedIndustryTypeList = [...industryTypeList].sort((a, b) => a.display.localeCompare(b.display));


    return (
      <div className="sidemenu-column-wrapper">
        {/* Industry Type Capabilities Section */}
        <div>
          <p style={{ paddingTop: '10px', fontWeight: 600 }}>
            Industry Type
          </p>
          <div className="sidemenu-choices-wrapper">
            {sortedIndustryTypeList.map(({ display, key }) => (
              <div className="sidemenu-switch-wrapper" key={key}>
                <p>{display}</p>
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={allSwitchItems.includes(key) ? (e => {
                    onChange(e, supplierSearchMenuCamelCase[key]);
                  }) : undefined}
                  checked={allSwitchItems.includes(key) ? supplierSearchCriteria[supplierSearchMenuCamelCase[key]] : false}
                  disabled={!allSwitchItems.includes(key)}
                />
              </div>
            ))}
          </div>
        </div>
        {/* Search Capabilities Section */}
        <div>
          <p style={{ paddingTop: '10px', fontWeight: 600 }}>
            Search Capabilities
          </p>
          <div className="sidemenu-choices-wrapper">
            {sortedFilteredSwitchItems.map((item) => (
              <div className="sidemenu-switch-wrapper" key={ensuredCamelCase[item]}>
                <p>{item}</p>
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={e => {
                    onChange(e, ensuredCamelCase[item]);
                  }}
                  checked={supplierSearchCriteria[ensuredCamelCase[item]]}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontWeight: 600 }}>Supplier Capabilities</p>
          <Collapse accordion>
            {orderedKeys.map((key) => (
              <Panel header={keyMap[key] || key} key={ensuredCamelCase[key]}>
                {(() => {
                  if (key === "Weight Range") {
                    return (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={`Select ${key}`}
                        optionLabelProp="label"
                        allowClear
                        onChange={e => {
                          onChange(e, ensuredCamelCase[key]);
                        }}
                        value={supplierSearchCriteria[ensuredCamelCase[key]]}
                      >
                        {this._formatClassifications(enhancedSupplierSearchMenuSelect[key], key).map(criterium => (
                          <Option value={criterium.value} label={criterium.value} key={criterium.value}>
                            {criterium.label}
                          </Option>
                        ))}
                      </Select>
                    )
                  } else if (enhancedSupplierSearchMenuSelect[key].length < 40) {
                    return (
                      <Checkbox.Group
                        onChange={e => {
                          onChange(e, ensuredCamelCase[key]);
                        }}
                        style={{ display: 'flex', flexDirection: 'column' }}
                        options={this._formatClassifications(enhancedSupplierSearchMenuSelect[key], key)}
                        value={supplierSearchCriteria[ensuredCamelCase[key]]}
                      />
                    )
                  }
                  return (
                    <Select
                      mode="multiple"
                      tagRender={tagRender}
                      style={{ width: '100%' }}
                      placeholder={`Select ${key}`}
                      optionLabelProp="label"
                      allowClear
                      onChange={e => {
                        onChange(e, ensuredCamelCase[key]);
                      }}
                      value={supplierSearchCriteria[ensuredCamelCase[key]]}
                    >
                      {this._formatClassifications(enhancedSupplierSearchMenuSelect[key], key).map(criterium => (
                        <Option value={criterium.value} label={criterium.value} key={criterium.value}>
                          {criterium.label}
                        </Option>
                      ))}
                    </Select>
                  )
                })()}
              </Panel>
            ))}
          </Collapse>
        </div>
      </div >
    );
  }
}

SideMenu.propTypes = {
  supplierAggs: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  supplierSearchCriteria: PropTypes.object.isRequired,
  supplierSearchMenuSwitch: PropTypes.object.isRequired,
  supplierSearchMenuSelect: PropTypes.object.isRequired,
  supplierSearchMenuCamelCase: PropTypes.object.isRequired,
};

export default SideMenu;



// WEBPACK FOOTER //
// ./src/components/SideMenu/SideMenu.jsx